/**
 * Linear Assets — Operations Dashboard
 *
 * Provides executive overview across power line & railway corridors:
 *   • Monitored corridor lengths & buffer zones
 *   • Real-time track obstruction alerts (≤ 6h SLA latency)
 *   • Predictive vegetation encroachment contact risks
 *   • Intelligence loop navigation (Steps 1–8)
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Zap, Train, AlertTriangle, ShieldAlert,
  Navigation, RefreshCw, Activity, ArrowRight, Clock,
  CheckCircle2, Compass, MapPin, Gauge, Layers, ShieldCheck, Scale, TrendingUp
} from 'lucide-react';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Spinner from '../../components/common/Spinner';
import Alert from '../../components/common/Alert';
import { KpiCard, StepPill, ActionBanner } from '../../components/common/OperationsDashboard';
import { useNavigate } from 'react-router-dom';
import { linearAssetsApi } from '../../services/api';

export const LinearAssetsDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState<string | null>(null);
  const [projections, setProjections]   = useState<any[]>([]);
  const [obstructions, setObstructions] = useState<any[]>([]);
  const [lastRefresh, setLastRefresh]   = useState(new Date());

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [projRes, obsRes] = await Promise.all([
        linearAssetsApi.getEncroachmentProjections(),
        linearAssetsApi.getTrackObstructions(),
      ]);
      setProjections(projRes.data?.projections || []);
      setObstructions(obsRes.data?.obstructions || []);
      setLastRefresh(new Date());
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to load linear assets intelligence');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const criticalObstructions = obstructions.filter(o => o.status === 'active').length;
  const criticalProjections = projections.filter(p => p.days_to_contact <= 60).length;
  const totalCorridors = 4;
  const totalKm = '845 km';

  const actionRequired = criticalObstructions + criticalProjections;

  return (
    <div className="space-y-6">
      {/* ── Header ── */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Zap className="w-7 h-7 text-yellow-500" /> Linear Assets Corridor Intelligence
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Power line &amp; railway right-of-way monitoring with predictive vegetation clearance and rapid obstruction alerts
          </p>
        </div>
        <div className="flex items-center gap-2">
          <p className="text-xs text-gray-400 dark:text-gray-500">
            Updated {lastRefresh.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
          </p>
          <Button variant="outline" onClick={loadData} disabled={loading}>
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {error && <Alert type="error" onClose={() => setError(null)}>{error}</Alert>}

      {/* ── Action Required Banner ── */}
      {!loading && <ActionBanner count={actionRequired} />}

      {loading ? (
        <div className="flex justify-center py-14">
          <Spinner size="lg" />
        </div>
      ) : (
        <>
          {/* ── KPI Strip ── */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <KpiCard
              label="Corridors Monitored"
              value={`${totalCorridors} (${totalKm})`}
              icon={MapPin}
              colour="bg-yellow-100 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400"
              onClick={() => navigate('/linear-assets/corridors')}
            />
            <KpiCard
              label="High Contact Risks (<60d)"
              value={criticalProjections}
              icon={TrendingUp}
              colour={criticalProjections > 0 ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400' : 'bg-gray-100 dark:bg-gray-700 text-gray-500'}
              onClick={() => navigate('/linear-assets/encroachment')}
            />
            <KpiCard
              label="Active Obstructions"
              value={criticalObstructions}
              icon={Train}
              colour={criticalObstructions > 0 ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400' : 'bg-gray-100 dark:bg-gray-700 text-gray-500'}
              onClick={() => navigate('/linear-assets/obstructions')}
            />
            <KpiCard
              label="Satellite Pass SLA"
              value="≤ 2.5 hrs"
              icon={Clock}
              colour="bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400"
            />
          </div>

          {/* ── Action Queue & Summary Metrics ── */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-3">
              <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Linear Assets 8-Step Intelligence Loop — Action Queue
              </p>
              <StepPill step="1"   label="Corridor Boundaries & Centreline Buffers" path="/linear-assets/corridors" />
              <StepPill step="2"   label="Temporal Analysis (NDVI & Thermal Trends)" path="/linear-assets/temporal" />
              <StepPill step="3–4" label="Vegetation Encroachment & Growth Modeling" count={criticalProjections} urgent path="/linear-assets/encroachment" />
              <StepPill step="4b"  label="Track Obstructions Rapid Triage (<6h SLA)"  count={criticalObstructions} urgent path="/linear-assets/obstructions" />
              <StepPill step="5"   label="Corridor Drone Survey Planner"             path="/linear-assets/survey-planner" />
              <StepPill step="6"   label="Flight Data & Diagnostics (LiDAR)"         path="/linear-assets/flight-analysis" />
              <StepPill step="7"   label="Analysis Review & Digital Seal Sign-off"   path="/linear-assets/analysis-review" />
              <StepPill step="8"   label="Clearance Response & Contractor Dispatch"  path="/linear-assets/response" />
              <StepPill step="8.6" label="Clearance Efficacy & Rescan Verification"  path="/linear-assets/efficacy" />
            </div>

            <div className="space-y-4">
              <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Corridor Safety Summary
              </p>

              <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
                <div className="flex items-center justify-between">
                  <p className="text-xs text-gray-500 dark:text-gray-400">Critical Obstructions</p>
                  {criticalObstructions > 0 && (
                    <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300">
                      SLA Alert
                    </span>
                  )}
                </div>
                <p className={`text-3xl font-bold mt-1 ${criticalObstructions > 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-white'}`}>
                  {criticalObstructions}
                </p>
              </div>

              <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
                <div className="flex items-center justify-between">
                  <p className="text-xs text-gray-500 dark:text-gray-400">Near-Term Contact Risk (&lt;60d)</p>
                  {criticalProjections > 0 && (
                    <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
                      High Risk
                    </span>
                  )}
                </div>
                <p className={`text-3xl font-bold mt-1 ${criticalProjections > 0 ? 'text-amber-600 dark:text-amber-400' : 'text-gray-900 dark:text-white'}`}>
                  {criticalProjections}
                </p>
              </div>

              <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
                <p className="text-xs text-gray-500 dark:text-gray-400">Clearance Work Orders</p>
                <p className="text-3xl font-bold mt-1 text-yellow-600 dark:text-yellow-400">3 Active</p>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">2 tree-trimming crews · 1 track team</p>
              </div>

              <div className="space-y-2">
                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Quick Actions</p>
                {[
                  { label: 'Work Orders & Audit Logs', path: '/linear-assets/work-orders', icon: Layers },
                  { label: 'Compliance Reports', path: '/linear-assets/reports', icon: Scale },
                  { label: 'Clearance Efficacy Rescan', path: '/linear-assets/efficacy', icon: ShieldCheck },
                ].map(({ label, path, icon: Icon }) => (
                  <button
                    key={path}
                    onClick={() => navigate(path)}
                    className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  >
                    <Icon className="w-4 h-4 text-yellow-500 flex-shrink-0" />
                    <span>{label}</span>
                    <span className="ml-auto text-gray-400">&rsaquo;</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* ── Active Hazard Feeds ── */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Track Obstruction Quick Monitor */}
            <Card className="p-4">
              <div className="flex items-center justify-between mb-3 pb-2 border-b border-gray-100 dark:border-gray-800">
                <h3 className="font-bold text-gray-900 dark:text-white flex items-center gap-2 text-sm">
                  <Train className="w-4 h-4 text-red-500" /> Track Obstruction Alerts (&lt;6h SLA)
                </h3>
                <Button variant="ghost" size="sm" onClick={() => navigate('/linear-assets/obstructions')}>
                  View All &rarr;
                </Button>
              </div>
              <div className="space-y-3">
                {obstructions.slice(0, 2).map((obs) => (
                  <div key={obs.id} className="p-3 rounded-lg border border-red-200 dark:border-red-900/50 bg-red-50/50 dark:bg-red-950/20 text-xs">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <span className="font-bold text-red-950 dark:text-red-200">{obs.corridor_name} &bull; Km {obs.track_km}</span>
                      <span className="px-2 py-0.5 rounded bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300 font-bold uppercase text-[10px]">
                        {obs.severity}
                      </span>
                    </div>
                    <p className="text-gray-700 dark:text-gray-300 mt-1">{obs.description}</p>
                    <div className="mt-2 text-red-700 dark:text-red-300 font-semibold flex items-center gap-1">
                      <ShieldAlert className="w-3.5 h-3.5" /> {obs.action_recommended}
                    </div>
                  </div>
                ))}
              </div>
            </Card>

            {/* Vegetation Encroachment Projection Quick Monitor */}
            <Card className="p-4">
              <div className="flex items-center justify-between mb-3 pb-2 border-b border-gray-100 dark:border-gray-800">
                <h3 className="font-bold text-gray-900 dark:text-white flex items-center gap-2 text-sm">
                  <Zap className="w-4 h-4 text-yellow-500" /> Predictive Growth Contact Risk (&lt;60d)
                </h3>
                <Button variant="ghost" size="sm" onClick={() => navigate('/linear-assets/encroachment')}>
                  View Matrix &rarr;
                </Button>
              </div>
              <div className="space-y-3">
                {projections.slice(0, 2).map((proj) => (
                  <div key={proj.corridor_id} className="p-3 rounded-lg border border-yellow-200 dark:border-yellow-900/50 bg-yellow-50/50 dark:bg-yellow-950/20 text-xs">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <span className="font-bold text-yellow-950 dark:text-yellow-200">{proj.corridor_name}</span>
                      <span className="px-2 py-0.5 rounded bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300 font-bold uppercase text-[10px]">
                        {proj.days_to_contact} Days to Contact
                      </span>
                    </div>
                    <p className="text-gray-700 dark:text-gray-300">
                      Encroachment between Km {proj.start_km} &ndash; {proj.end_km} ({proj.length_m}m). Current clearance: {proj.current_distance_to_centreline_m}m.
                    </p>
                    <div className="mt-2 text-yellow-800 dark:text-yellow-300 font-semibold flex items-center gap-1">
                      <Clock className="w-3.5 h-3.5" /> Contact Projected: {proj.projected_contact_date}
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </>
      )}
    </div>
  );
};

export default LinearAssetsDashboardPage;
